// src/pages/content-management/helpdesk/admin/index.tsx
import { useState, useRef } from 'react'
import { Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PageHeader, showSuccessToast } from '@/components/common'
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog'
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from '@/components/ui'
import { useDocuments } from '@/hooks/useDocuments'
import { mockAiConfig } from '@/mocks/helpdesk-ai-config'
import { mockQuestionStats } from '@/mocks/helpdesk-question-stats'
import { DocumentTab } from '@/pages/content-management/helpdesk/document-tab'
import { SettingsTab } from '@/pages/content-management/helpdesk/settings-tab'
import { StatisticsTab } from '@/pages/content-management/helpdesk/statistics-tab'
import type { Document } from '@/types/document'

export interface QuestionStat {
    question: string;
    count: number;
    averageRating: number;
    lastAsked: Date;
}

export interface AiConfig {
    modelName: string;
    temperature: number;
    maxTokens: number;
    topP: number;
    frequencyPenalty: number;
    presencePenalty: number;
    documentSearchEnabled: boolean;
    webSearchEnabled: boolean;
    feedbackCollection: boolean;
}

// Import tab components

const HelpdeskAdminPage = () => {
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [activeTab, setActiveTab] = useState('documents')
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
    const [documentToDelete, setDocumentToDelete] = useState<Document | Document[] | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const { documents, deleteDocument, addDocument } = useDocuments()
    const [questionStats] = useState<QuestionStat[]>(mockQuestionStats)
    const [aiConfig, setAiConfig] = useState<AiConfig>(mockAiConfig)

    const handleDeleteDocumentClick = (doc: Document | Document[]) => {
        setDocumentToDelete(doc)
        setDeleteDialogOpen(true)
    }

    const confirmDeleteDocument = () => {
        if (documentToDelete) {
            if (Array.isArray(documentToDelete)) {
                documentToDelete.forEach(doc => deleteDocument(doc.id))
            } else {
                deleteDocument(documentToDelete.id)
            }
            setDocumentToDelete(null)
            setDeleteDialogOpen(false)
            setSelectedItems(new Set())
            if (Array.isArray(documentToDelete)) {
                showSuccessToast(t('success.helpdesk.documentsDeleted', { count: documentToDelete.length }))
            } else {
                showSuccessToast(t('success.helpdesk.documentDeleted'))
            }
        }
    }

    const handleSaveConfig = () => {
        showSuccessToast(t('success.helpdesk.configSaved'))
    }

    const handleUploadButtonClick = () => {
        fileInputRef.current?.click()
    }

    // Calculate pagination for documents
    const totalPages = Math.ceil(documents.length / pageSize)
    const startIndex = (currentPage - 1) * pageSize
    const paginatedDocuments = documents.slice(startIndex, startIndex + pageSize)

    return (
        <div className="flex flex-col gap-6">
            <PageHeader
                title={t('helpdesk.admin.title')}
                description={t('helpdesk.admin.description')}
                action={activeTab === 'documents' ? {
                    label: t('helpdesk.admin.uploadDocument'),
                    icon: Download,
                    onClick: () => handleUploadButtonClick()
                } : undefined}
            />

            <Tabs defaultValue="documents" className="w-full" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full md:w-1/2 grid-cols-3 mb-3">
                    <TabsTrigger value="documents">{t('helpdesk.admin.documents')}</TabsTrigger>
                    <TabsTrigger value="statistics">{t('helpdesk.admin.statistics')}</TabsTrigger>
                    <TabsTrigger value="settings">{t('helpdesk.admin.settings')}</TabsTrigger>
                </TabsList>

                <TabsContent value="documents">
                    <DocumentTab
                        paginatedDocuments={paginatedDocuments}
                        selectedItems={selectedItems}
                        setSelectedItems={setSelectedItems}
                        onDeleteDocumentClick={handleDeleteDocumentClick}
                        t={t}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        fileInputRef={fileInputRef}
                        onAddDocument={addDocument}
                    />
                </TabsContent>

                <TabsContent value="statistics">
                    <StatisticsTab questionStats={questionStats} t={t} />
                </TabsContent>

                <TabsContent value="settings">
                    <SettingsTab aiConfig={aiConfig} setAiConfig={setAiConfig} onSaveConfig={handleSaveConfig} t={t} />
                </TabsContent>
            </Tabs>

            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={confirmDeleteDocument}
                title={t('helpdesk.admin.confirmDeleteTitle')}
                description={t('helpdesk.admin.confirmDeleteDescription', {
                    documentName: Array.isArray(documentToDelete)
                        ? t('helpdesk.admin.multipleDocuments', { count: documentToDelete.length })
                        : documentToDelete?.name ?? ''
                })}
            />
        </div>
    )
}

export default HelpdeskAdminPage